import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AuthService } from '@app/modules/auth/auth.service';
import { SnackBarService } from '@app/shared/components/snack-bar/snack-bar.service';
import { TokenService } from '../services/token.service';

function handleTokenRefresh(
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  tokenService: TokenService,
  snackbarService: SnackBarService,
): Observable<HttpEvent<any>> {
  return authService.refreshToken(tokenService.getRefreshToken()).pipe(
    switchMap((response) => {
      if (response?.access) tokenService.setAccessToken(response.access);
      const retryReq = req.clone({
        setHeaders: { Authorization: `Bearer ${response.access}` },
      });
      return next(retryReq);
    }),
    catchError((err) => {
      snackbarService
        .showSnackbar('Token expirado!', 'Faça login novamente.', 3000)
        .afterDismissed()
        .subscribe(() => authService.logout());
      return throwError(() => err);
    })
  );
}

export function checkpathsToRefresh(req: HttpRequest<any>, pathsToRefresh: string[]) {
  return pathsToRefresh.some((path) => req.url.includes(path));
}

export function authInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const tokenService = inject(TokenService);
  const authService = inject(AuthService);
  const snackbarService = inject(SnackBarService);

  const accessToken = tokenService.getAccessToken();

  // 0) Não interceptar o próprio refresh
  if (req.url.includes('/access/refresh')) {
    return next(req);
  }

  // 1) Paths que exigem pré-refresh
  const pathsToRefresh = [
    '/datamanager/upload-data/',
    '/cloud/files/upload/',
    'cloud/files/download/',
    '/transfer/uploads/',
  ];
  const NEEDS_PREFRESH = accessToken
    && tokenService.isTokenWillExpireByTime(650) // 10 min
    && checkpathsToRefresh(req as HttpRequest<any>, pathsToRefresh);

  if (NEEDS_PREFRESH) {
    // Faz refresh ANTES de mandar o upload
    return handleTokenRefresh(req as HttpRequest<any>, next, authService, tokenService, snackbarService);
  }

  // 2) Fluxo normal: se tem token válido (>10min sobra), injeta e segue
  if (accessToken && !tokenService.isTokenExpired()) {
    const authed = (req as HttpRequest<any>).clone({
      setHeaders: { Authorization: `Bearer ${accessToken}` },
    });
    return next(authed).pipe(
      // 3) Se ainda assim der 401 (corrida), tenta 1x refresh e refaz
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return handleTokenRefresh(req as HttpRequest<any>, next, authService, tokenService, snackbarService);
        }
        if (error.status === 500) {
          snackbarService
            .showSnackbar('Erro de autenticação!', 'Faça login novamente.', 3000)
            .afterDismissed()
            .subscribe(() => authService.logout());
        }
        return throwError(() => error);
      })
    );
  }

  // 3) Sem token: segue e trata erro depois
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 || error.status === 500) {
        snackbarService
          .showSnackbar('Token expirado!', 'Faça login novamente.', 3000)
          .afterDismissed()
          .subscribe(() => authService.logout());
      }
      return throwError(() => error);
    }),
  );
}
